import {
  Building2,
  Calendar,
  Copy,
  Edit2,
  Hash,
  Mail,
  RefreshCw,
  Shield,
  UserPlus,
  Users,
} from "@digital-twins/design-system/icons";
import { Button } from "@design-system/components/button";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { getUserInitials } from "@/lib/utils/avatar";
import { toast as notify } from "sonner";


import { Skeleton } from "@design-system/components/skeleton";
import { TabsContent } from "@design-system/components/tabs";
import { Text } from "@design-system/components/Text";
import type { AccountTabProps } from "@/types/components/settings/account-tab";
import { TeamMembersTable } from "./TeamMembersTable";
import { useTranslation } from "react-i18next";

export function AccountTab({
  profileLoading,
  profile,
  organization,
  canInvite,
  onEdit,
  onInvite,
  onRegenerateAvatar,
  canCustomizeAvatarColor,
  avatarAppearanceProps,
  formatDate,
  refreshProfile,
  members,
  invites,
  membersLoading,
  isAdmin,
  currentUserId,
  onResendInvite,
  onRevokeInvite,
  onRemoveMember,
}: AccountTabProps) {
  const { t } = useTranslation("app");
  const isPersonalOrg = organization?.type === 'personal';
  const showTeamTable =
    organization && !isPersonalOrg && currentUserId;
  const showStandaloneInvite = isPersonalOrg && canInvite;
  return (
    <TabsContent value="account" className="space-y-6">
      {profileLoading ? (
        <div className="flex flex-col md:flex-row gap-6 md:gap-10 rounded-xl bg-card p-4 md:p-8">
          <div className="flex flex-col items-center gap-3">
            <Skeleton className="h-32 w-32 rounded-full" />
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="flex-1">
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row md:gap-16 rounded-xl bg-card p-4 md:p-8 gap-6">
          {/* Left: Avatar + name + email */}
          <div className="flex flex-col items-center gap-5 md:pt-2">
            <div className="group relative">
              <UserAvatar
                initials={getUserInitials(profile?.name, profile?.email)}
                name={profile?.name || t("account.userAvatar")}
                size="lg"
                className="!h-32 !w-32 transition-opacity group-hover:opacity-75 text-[40px]"
                {...avatarAppearanceProps}
              />
              {canCustomizeAvatarColor && (
              <button
                onClick={onRegenerateAvatar}
                className="absolute inset-0 rounded-full bg-foreground/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                title={t("nav.changeAvatarColor")}
              >
                <RefreshCw
                  className="h-8 w-8 text-background"
                  strokeWidth={2}
                />
              </button>
              )}
            </div>
            <div className="text-center">
              <div className="flex items-center gap-1.5">
                <Text size="base" weight="medium">{profile?.name || t("account.noName")}</Text>
                <button
                  onClick={onEdit}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1 -m-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title={t("account.editName")}
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <Text size="sm" color="muted">{profile?.email}</Text>
            </div>
          </div>

          {/* Right: Account details */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-5 content-center">
            <InfoRow icon={Mail} label={t("account.email")} value={profile?.email || "–"} />
            <InfoRow icon={Calendar} label={t("account.memberSince")} value={profile?.createdAt ? formatDate(profile.createdAt) : "–"} />

            {organization && (
              <InfoRow icon={Building2} label={t("account.organization")} value={organization.name} />
            )}

            {organization && (
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 shrink-0 rounded-lg bg-muted/40 flex items-center justify-center">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <Text size="xs" color="muted">{t("account.organizationId")}</Text>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(organization.id);
                      notify.success(t("account.organizationIdCopied"));
                    }}
                    className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-foreground/80 transition-colors"
                    title={organization.id}
                  >
                    <span className="font-mono">
                      {organization.id.slice(0, 8)}&hellip;
                    </span>
                    <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}

            {organization && (
              <InfoRow icon={Shield} label={t("account.organizationType")} value={t(`account.orgTypes.${organization.type}`, { defaultValue: organization.type })} />
            )}

            {organization && (
              <InfoRow icon={Users} label={t("account.yourRole")} value={t(`account.roles.${organization.orgRole}`, { defaultValue: organization.orgRole })} />
            )}
          </div>
        </div>
      )}

      {showStandaloneInvite && onInvite && (
        <div className="flex items-center justify-between">
          <Text size="sm" weight="medium">
            {t("account.teamMembers")}
          </Text>
          <Button
            variant="outline"
            size="sm"
            onClick={onInvite}
            data-testid="personal-invite-button"
            className="h-[30px] rounded-[6px] border-border px-4 text-[14px] font-medium text-foreground hover:bg-muted"
          >
            <UserPlus className="mr-1.5 h-3.5 w-3.5" />
            {t("account.invite")}
          </Button>
        </div>
      )}

      {showTeamTable && (
        <TeamMembersTable
          members={members || []}
          invites={invites || []}
          currentUserId={currentUserId!}
          isAdmin={isAdmin || false}
          loading={membersLoading}
          canInvite={canInvite}
          onInvite={onInvite}
          onResendInvite={onResendInvite || (async () => {})}
          onRevokeInvite={onRevokeInvite || (async () => {})}
          onRemoveMember={onRemoveMember || (async () => {})}
        />
      )}
    </TabsContent>
  );
}

function InfoRow({ icon: Icon, label, value, children }: { icon: any; label: string; value?: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 shrink-0 rounded-lg bg-muted/40 flex items-center justify-center">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div>
        <Text size="xs" color="muted">{label}</Text>
        {children ?? <Text size="sm" weight="medium">{value}</Text>}
      </div>
    </div>
  );
}
